/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import type { FloorPlanMetrics } from './rfFloorMetrics'

export const MAX_INTERF_RISE_PCT = 5
export const MAX_WEAK_RISE_PCT = 5
export const MAX_GOOD_DROP_PCT = 8

export function interferenceDominant(metrics: FloorPlanMetrics): boolean {
  if (metrics.highInterfPct >= 40) return true
  return metrics.highInterfPct >= 25 && metrics.weakPct <= 20
}

export function interferenceReliefNeeded(metrics: FloorPlanMetrics): boolean {
  return metrics.highInterfPct >= 15
}

export function coverageDominant(metrics: FloorPlanMetrics): boolean {
  return metrics.weakPct >= 20 && metrics.highInterfPct <= 15
}

export function coverageOk(metrics: FloorPlanMetrics, before: FloorPlanMetrics): boolean {
  return (
    metrics.weakPct <= before.weakPct + MAX_WEAK_RISE_PCT
    && metrics.goodPct >= before.goodPct - MAX_GOOD_DROP_PCT
  )
}

export function interferenceWithinCap(metrics: FloorPlanMetrics, before: FloorPlanMetrics): boolean {
  return metrics.highInterfPct <= before.highInterfPct + MAX_INTERF_RISE_PCT
}

/** Hard gate: never apply a plan that violates coverage or interference caps vs baseline. */
export function planAcceptable(metrics: FloorPlanMetrics, before: FloorPlanMetrics): boolean {
  return coverageOk(metrics, before) && interferenceWithinCap(metrics, before)
}

/** True when the plan measurably helps at least one KPI without breaking caps. */
export function planImproves(metrics: FloorPlanMetrics, before: FloorPlanMetrics): boolean {
  if (!planAcceptable(metrics, before)) return false
  const weakGain = before.weakPct - metrics.weakPct
  const goodGain = metrics.goodPct - before.goodPct
  const interfGain = before.highInterfPct - metrics.highInterfPct
  const interfMin = before.highInterfPct >= 25 ? 1 : 2
  return weakGain >= 1.5 || goodGain >= 1.5 || interfGain >= interfMin
}

export function candidateRank(metrics: FloorPlanMetrics, before: FloorPlanMetrics): number {
  const weakPenalty = Math.max(0, metrics.weakPct - before.weakPct) * 1.2
  const goodPenalty = Math.max(0, before.goodPct - metrics.goodPct) * 0.9
  const interfGain = before.highInterfPct - metrics.highInterfPct
  const interfWeight = before.highInterfPct >= 25 ? 0.55 : 0.25
  return metrics.score - weakPenalty - goodPenalty + interfGain * interfWeight
}
